import { fromZonedTime, formatInTimeZone } from "date-fns-tz";
import { parseUtc } from "../common/datetimeUtils.js";

export const MIN_RPE_SESSION_TARGET_PLAYERS = 5;
export const RPE_TREND_HISTORY_DAYS = 7;
const RPE_RESET_HOUR = 8;
const RPE_SPORT_TZ = "Europe/Paris";

function shiftIsoDate(isoDate, deltaDays) {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/** Daily RPE window in UTC (08:00 Europe/Paris → next day 08:00), aligned with backend. */
export function rpeSportDayWindowUtc(rpeDateIso) {
  if (!rpeDateIso) return null;
  const hour = String(RPE_RESET_HOUR).padStart(2, "0");
  const start = fromZonedTime(`${rpeDateIso}T${hour}:00:00`, RPE_SPORT_TZ);
  const nextDay = shiftIsoDate(rpeDateIso, 1);
  const end = fromZonedTime(`${nextDay}T${hour}:00:00`, RPE_SPORT_TZ);
  return { start, end };
}

export function timestampInRpeSportDay(isoValue, rpeDateIso) {
  if (!rpeDateIso) return true;
  const ts = parseUtc(isoValue);
  if (!ts) return false;
  const window = rpeSportDayWindowUtc(rpeDateIso);
  if (!window) return false;
  return ts >= window.start && ts < window.end;
}

export function resolveRpeSportDayForTimestamp(isoValue) {
  const ts = parseUtc(isoValue ?? new Date().toISOString()) || new Date();
  const iso = ts.toISOString();
  const parisDate = formatInTimeZone(ts, RPE_SPORT_TZ, "yyyy-MM-dd");
  if (timestampInRpeSportDay(iso, parisDate)) return parisDate;
  const previous = shiftIsoDate(parisDate, -1);
  if (timestampInRpeSportDay(iso, previous)) return previous;
  return parisDate;
}

export function currentRpeSportDayIso() {
  return resolveRpeSportDayForTimestamp(new Date().toISOString());
}

export function normalizeRpeSessionId(sessionId) {
  if (sessionId == null || sessionId === "") return null;
  return String(sessionId);
}

export function sortRpeSessionsChronologically(sessions = []) {
  return [...sessions].sort((a, b) => {
    const ta = Date.parse(a?.created_at ?? "") || 0;
    const tb = Date.parse(b?.created_at ?? "") || 0;
    if (ta !== tb) return ta - tb;
    return normalizeRpeSessionId(a?.session_id).localeCompare(normalizeRpeSessionId(b?.session_id));
  });
}

function sessionTimestamp(session) {
  return Date.parse(session?.created_at ?? "") || 0;
}

function sessionResponseCount(session) {
  const n = Number(session?.responses_count);
  return Number.isFinite(n) ? n : 0;
}

/** Default session prefers populated and most recent sessions. */
export function pickDefaultRpeSessionId(sessions = []) {
  const ordered = sortRpeSessionsChronologically(sessions);
  if (!ordered.length) return null;

  const best = ordered.reduce((currentBest, candidate) => {
    if (!currentBest) return candidate;

    const bestHasAnswers = sessionResponseCount(currentBest) > 0;
    const candidateHasAnswers = sessionResponseCount(candidate) > 0;
    if (candidateHasAnswers !== bestHasAnswers) {
      return candidateHasAnswers ? candidate : currentBest;
    }

    const bestTs = sessionTimestamp(currentBest);
    const candidateTs = sessionTimestamp(candidate);
    if (candidateTs !== bestTs) {
      return candidateTs > bestTs ? candidate : currentBest;
    }

    const bestId = normalizeRpeSessionId(currentBest?.session_id) ?? "";
    const candidateId = normalizeRpeSessionId(candidate?.session_id) ?? "";
    return candidateId.localeCompare(bestId) >= 0 ? candidate : currentBest;
  }, null);

  return normalizeRpeSessionId(best?.session_id);
}

export function mergeRpeSessionsForDay(daySessions = [], supplementalSessions = [], rpeDate = null) {
  const map = new Map();

  const upsertSession = (sessionId, patch) => {
    const existing = map.get(sessionId) || {};
    const closedAt = patch.closed_at !== undefined ? patch.closed_at : existing.closed_at ?? null;
    const isOpen = patch.is_open !== undefined
      ? patch.is_open
      : (closedAt == null && existing.is_open !== false);
    map.set(sessionId, {
      ...existing,
      ...patch,
      session_id: sessionId,
      closed_at: closedAt,
      is_open: isOpen,
    });
  };

  daySessions.forEach((session) => {
    const sessionId = normalizeRpeSessionId(session?.session_id);
    if (!sessionId) return;
    upsertSession(sessionId, { ...session });
  });

  (supplementalSessions || []).forEach((extra) => {
    const sessionId = normalizeRpeSessionId(extra?.session_id);
    if (!sessionId) return;

    if (rpeDate && !timestampInRpeSportDay(extra?.created_at, rpeDate)) return;

    const existing = map.get(sessionId) || {};
    upsertSession(sessionId, {
      ...existing,
      created_at: extra?.created_at ?? existing.created_at ?? null,
      closed_at: extra?.closed_at ?? existing.closed_at ?? null,
      with_hooper: extra?.with_hooper ?? existing.with_hooper ?? false,
      coach_rpe_value: extra?.coach_rpe_value ?? existing.coach_rpe_value ?? null,
      target_player_ids: extra?.target_player_ids ?? existing.target_player_ids ?? [],
      responses_count:
        existing.responses_count
        ?? extra?.responses_count
        ?? (Array.isArray(extra?.responders) ? extra.responders.length : 0),
      is_open: extra?.is_open ?? (extra?.closed_at == null),
      label: extra?.label ?? existing.label ?? null,
    });
  });

  return sortRpeSessionsChronologically([...map.values()]);
}

export function resolveRpeSessionSelection(requestedId, sessions = [], apiSelectedId = null) {
  const normalizedRequested = normalizeRpeSessionId(requestedId);
  if (
    normalizedRequested
    && sessions.some((session) => normalizeRpeSessionId(session.session_id) === normalizedRequested)
  ) {
    return normalizedRequested;
  }

  const normalizedApi = normalizeRpeSessionId(apiSelectedId);
  if (
    normalizedApi
    && sessions.some((session) => normalizeRpeSessionId(session.session_id) === normalizedApi)
  ) {
    return normalizedApi;
  }

  return pickDefaultRpeSessionId(sessions);
}
